import React, { useEffect, useRef, useState } from 'react';
import { TYPE, useTheme } from '../../theme';
import { Button, Dialog, Field, Input } from '../ui';
import { auth, type QidUser } from '../../api';
import {
  INVITE_REQUIRED_EVENT, InviteRequiredError, clearInviteCodeRequest, inviteCodePending, pendingMarkInvite,
} from './inviteRequired';

// IK1 · Visas när Qid-inloggningen (e-post, Google eller Apple) lyckats men Qvenari-kontot saknas.
// Samma token används när koden lösts in, så ingen behöver logga in igen. Avbryt loggar ut.

export function InviteCodeGate({ onDone }: { onDone: (u: QidUser) => void }) {
  const T = useTheme();
  const [open, setOpen] = useState(() => inviteCodePending() && auth.isLoggedIn());
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const triedLink = useRef(false);

  useEffect(() => {
    const show = () => { if (auth.isLoggedIn()) setOpen(true); };
    window.addEventListener(INVITE_REQUIRED_EVENT, show);
    return () => window.removeEventListener(INVITE_REQUIRED_EVENT, show);
  }, []);

  const done = (u: QidUser) => { clearInviteCodeRequest(); setOpen(false); setCode(''); setErr(''); onDone(u); };

  // Inbjudningslänk till en mark släpper in utan kod — prova den först, tyst.
  useEffect(() => {
    if (!open || triedLink.current || !pendingMarkInvite()) return;
    triedLink.current = true;
    setLoading(true);
    auth.redeemInvite().then(done).catch(() => {}).finally(() => setLoading(false));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim()) { setErr('Ange invitekoden'); return; }
    setLoading(true); setErr('');
    try {
      done(await auth.redeemInvite(code));
    } catch (ex) {
      setErr(ex instanceof InviteRequiredError ? ex.message : ex instanceof Error ? ex.message : 'Något gick fel');
    } finally { setLoading(false); }
  };

  const cancel = () => { clearInviteCodeRequest(); auth.clearTokens(); setOpen(false); setCode(''); setErr(''); };

  if (!open) return null;
  return (
    <Dialog open onClose={cancel} title="Ange invitekod"
      actions={<>
        <Button variant="secondary" onClick={cancel} disabled={loading}>Avbryt</Button>
        <Button type="submit" form="qv-invite-form" loading={loading}>Fortsätt</Button>
      </>}>
      <form id="qv-invite-form" onSubmit={submit}>
        <p style={{ ...TYPE.body, color: T.textSub, margin: '0 0 14px' }}>
          Qvenari är i stängd beta. Du är inloggad, men för att skapa ditt Qvenari-konto behöver du en invitekod.
        </p>
        <Field label="Invitekod" error={err || undefined}>
          <Input value={code} onChange={e => { setCode(e.target.value); setErr(''); }} autoFocus autoComplete="off"
            autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        </Field>
        {/* Enter i fältet skickar formuläret även när knapparna ligger utanför det (mobilens Sheet). */}
        <button type="submit" hidden aria-hidden="true" tabIndex={-1} />
      </form>
    </Dialog>
  );
}
